import {
  ACCOUNT_TYPES,
  DB_ACCOUNT_TYPES,
  MENUS,
  US_DATE_SHORT,
  formatDate,
  getClientPage,
  getClientPageIndex,
} from '@/common/constants';

import { mapGetters } from 'vuex';

const UnathenticatedClientMixin = {
  data: () => ({}),

  mixins: [],

  computed: {
    ...mapGetters([
      "clientProgress",
      "clientData",
      "interviewPages",
      "isAuthenticated",
      "isClientAuthenticated",
    ]),

    /* Page Completions and Step Counts */
    isCitizenshipComplete() {
      return this.isPageComplete(MENUS.CLIENT.CITIZENSHIP.id);
    },
    isLegalNameComplete() {
      return this.isPageComplete(MENUS.CLIENT.LEGAL_NAME.id);
    },
    isLegalAddressComplete() {
      return this.isPageComplete(MENUS.CLIENT.LEGAL_ADDRESS.id);
    },
    legalCompletionSteps() {
      let c = 0;
      if (this.isCitizenshipComplete) c++;
      if (this.isLegalNameComplete) c++;
      if (this.isLegalAddressComplete) c++;
      return c;
    },
    isLegalComplete() {
      return this.legalCompletionSteps === 3;
    },
    isCustomQuestionComplete({ questionId }) {
      return this.isPageComplete(
        MENUS.CLIENT.CUSTOM_QUESTIONS.id,
        null,
        questionId
      );
    },
    companyLogo() {
      if (this.clientProgress) return this.clientProgress.companyLogo;
      return null;
    },
    companyName() {
      if (this.clientProgress) return this.clientProgress.companyName;
      return "";
    },
    areCustomQuestionsComplete() {
      // If we don't have any client progress data, assume we're not complete
      if (!this.clientProgress || !this.clientProgress.progress) {
        return false;
      }

      // Get all custom question pages
      const customQuestions = this.clientProgress.progress.filter(
        (page) => page.pageName === MENUS.CLIENT.CUSTOM_QUESTIONS.id
      );

      // If there are no custom questions, they're technically "complete"
      if (customQuestions.length === 0) {
        return true;
      }

      // Check that all custom questions have been filled and are fully synced
      return customQuestions.every(
        (page) => page.lastCompleted !== null && page.status === "SYNCED"
      );
    },
    isSSNComplete() {
      return this.isPageComplete(MENUS.CLIENT.SSN.id);
    },
    isDOBComplete() {
      return this.isPageComplete(MENUS.CLIENT.DOB.id);
    },
    isAlternatePhoneComplete() {
      return this.isPageComplete(MENUS.CLIENT.ALTERNATE_PHONE.id);
    },
    basicCompletionSteps() {
      let c = 0;
      if (this.isSSNComplete) c++;
      if (this.isDOBComplete) c++;
      if (this.isAlternatePhoneComplete) c++;
      return c;
    },
    isBasicComplete() {
      return this.basicCompletionSteps === 3;
    },
    isEmploymentStatusComplete() {
      return this.isPageComplete(MENUS.CLIENT.EMPLOYMENT_STATUS.id);
    },
    isEmploymentStatus2Complete() {
      return this.isPageComplete(MENUS.CLIENT.EMPLOYMENT_STATUS_2.id);
    },
    isCompanyAddressComplete() {
      return this.isPageComplete(MENUS.CLIENT.COMPANY_ADDRESS.id);
    },
    isPershingCompanyAddressComplete() {
      return this.isPageComplete(MENUS.CLIENT.PERSHING_COMPANY_ADDRESS.id);
    },
    isJobDescriptionComplete() {
      return this.isPageComplete(MENUS.CLIENT.JOB_DESCRIPTION.id);
    },
    isCompanyOfficialComplete() {
      return this.isPageComplete(MENUS.CLIENT.COMPANY_OFFICIAL.id);
    },
    isBeneficialInterestComplete() {
      return this.isPageComplete(MENUS.CLIENT.BENEFICIAL_INTEREST.id);
    },
    isAnnualIncomeComplete() {
      return this.isPageComplete(MENUS.CLIENT.ANNUAL_INCOME.id);
    },
    isBusinessLineComplete() {
      return this.isPageComplete(MENUS.CLIENT.BUSINESS.id);
    },
    isWealthSourceComplete() {
      return this.isPageComplete(MENUS.CLIENT.WEALTH_SOURCE.id);
    },
    isPePComplete() {
      return this.isPageComplete(MENUS.CLIENT.POLITICALLY_EXPOSED_PERSON.id);
    },
    isConsolidatedStatementComplete() {
      return this.isPageComplete(MENUS.CLIENT.CONSOLIDATED_STATEMENT.id);
    },
    isPershingConsolidatedStatementComplete() {
      return this.isPageComplete(MENUS.CLIENT.PERSHING_CONSOLIDATED_STATEMENT.id);
    },
    isPershingMaritalStatusComplete() {
      return this.isPageComplete(MENUS.CLIENT.PERSHING_MARITAL_STATUS.id);
    },
    isPershingEmployerRiskComplete() {
      return this.isPageComplete(MENUS.CLIENT.PERSHING_EMPLOYER_RISK.id);
    },
    totalEmploymentSteps() {
      let c = 3;

      /* Only add company and job pages if present */
      const hasCompanyPage =
        this.getPage(MENUS.CLIENT.COMPANY_ADDRESS.id) !== undefined ||
        this.getPage(MENUS.CLIENT.PERSHING_COMPANY_ADDRESS.id) !== undefined;
      if (hasCompanyPage) c++;

      const hasJobPage =
        this.getPage(MENUS.CLIENT.JOB_DESCRIPTION.id) !== undefined;
      if (hasJobPage) c++;

      return c;
    },
    employmentCompletionSteps() {
      let c = 0;
      if (this.isEmploymentStatusComplete) c++;

      /* Only count company and job pages if present */
      const hasCompanyPage =
        this.getPage(MENUS.CLIENT.COMPANY_ADDRESS.id) !== undefined ||
        this.getPage(MENUS.CLIENT.PERSHING_COMPANY_ADDRESS.id) !== undefined;
      if (hasCompanyPage && this.isCompanyAddressComplete) c++;

      const hasJobPage =
        this.getPage(MENUS.CLIENT.JOB_DESCRIPTION.id) !== undefined;
      if (hasJobPage && this.isJobDescriptionComplete) c++;

      if (this.isCompanyOfficialComplete) c++;
      if (this.isBeneficialInterestComplete) c++;
      return c;
    },
    isEmploymentComplete() {
      return this.employmentCompletionSteps === this.totalEmploymentSteps;
    },
    // === Inherited IRA (Decedent) helpers ===
    decedentPages() {
      if (!this.clientProgress || !this.clientProgress.progress) return [];
      return this.clientProgress.progress.filter(
        (p) =>
          p.pageName === MENUS.CLIENT.INHERITEDIRA.id ||
          p.pageName === MENUS.CLIENT.PERSHING_DECEDENT.id
      );
    },
    totalDecedentSteps() {
      return this.decedentPages.length;
    },
    decedentCompletionSteps() {
      return this.decedentPages.filter(
        (p) => p.lastCompleted && p.status === "SYNCED"
      ).length;
    },
    isDecedentComplete() {
      return (
        this.totalDecedentSteps > 0 &&
        this.decedentCompletionSteps === this.totalDecedentSteps
      );
    },
    canShowDecedent() {
      return this.totalDecedentSteps > 0;
    },
    firstDecedentRoute() {
      if (!this.canShowDecedent) return null;
      const firstIncomplete = this.decedentPages.find(
        (p) => !(p.lastCompleted && p.status === "SYNCED")
      );
      const pageToUse = firstIncomplete || this.decedentPages[0];
      return {
        name: pageToUse.pageName,
        params: { name: pageToUse.instance, label: pageToUse.label },
      };
    },
    isDecedentRoute() {
      return (
        this.$route.name === MENUS.CLIENT.INHERITEDIRA.id ||
        this.$route.name === MENUS.CLIENT.PERSHING_DECEDENT.id
      );
    },
    // === Trust Account helpers ===
    trustAccountPages() {
      if (!this.clientProgress || !this.clientProgress.progress) return [];
      // Include all trust-related pages
      return this.clientProgress.progress.filter(
        (p) =>
          p.pageName === MENUS.CLIENT.TRUST_INFO.id ||
          p.pageName === MENUS.CLIENT.PERSHING_TRUST_INFO.id ||
          p.pageName === MENUS.CLIENT.TRUSTEES.id ||
          p.pageName === MENUS.CLIENT.GRANTORS.id
      );
    },
    totalTrustAccountSteps() {
      return this.trustAccountPages.length;
    },
    trustAccountCompletionSteps() {
      return this.trustAccountPages.filter(
        (p) => p.lastCompleted && p.status === "SYNCED"
      ).length;
    },
    isTrustAccountsComplete() {
      // Only complete if we have trust pages and all are complete
      if (this.totalTrustAccountSteps === 0) return true;
      return this.trustAccountCompletionSteps === this.totalTrustAccountSteps;
    },
    canShowTrustAccount() {
      return this.totalTrustAccountSteps > 0;
    },
    firstTrustAccountRoute() {
      if (!this.canShowTrustAccount) return null;
      const firstIncomplete = this.trustAccountPages.find(
        (p) => !(p.lastCompleted && p.status === "SYNCED")
      );
      const pageToUse = firstIncomplete || this.trustAccountPages[0];
      return {
        name: pageToUse.pageName,
        params: { name: pageToUse.instance, label: pageToUse.label },
      };
    },
    isTrustAccountRoute() {
      return (
        this.$route.name === MENUS.CLIENT.TRUST_INFO.id ||
        this.$route.name === MENUS.CLIENT.PERSHING_TRUST_INFO.id ||
        this.$route.name === MENUS.CLIENT.TRUSTEES.id ||
        this.$route.name === MENUS.CLIENT.GRANTORS.id
      );
    },
    // === Entity Account helpers ===
    entityAccountPages() {
      if (!this.clientProgress || !this.clientProgress.progress) return [];
      // Include all entity-related pages
      return this.clientProgress.progress.filter(
        (p) =>
          p.pageName === MENUS.CLIENT.ENTITY_INFO.id ||
          p.pageName === MENUS.CLIENT.AUTHORIZED_SIGNERS.id
      );
    },
    totalEntityAccountSteps() {
      return this.entityAccountPages.length;
    },
    entityAccountCompletionSteps() {
      return this.entityAccountPages.filter(
        (p) => p.lastCompleted && p.status === "SYNCED"
      ).length;
    },
    isEntityAccountsComplete() {
      // Only complete if we have entity pages and all are complete
      if (this.totalEntityAccountSteps === 0) return true;
      return this.entityAccountCompletionSteps === this.totalEntityAccountSteps;
    },
    canShowEntityAccount() {
      return this.totalEntityAccountSteps > 0;
    },
    firstEntityAccountRoute() {
      if (!this.canShowEntityAccount) return null;
      const firstIncomplete = this.entityAccountPages.find(
        (p) => !(p.lastCompleted && p.status === "SYNCED")
      );
      const pageToUse = firstIncomplete || this.entityAccountPages[0];
      return {
        name: pageToUse.pageName,
        params: { name: pageToUse.instance, label: pageToUse.label },
      };
    },
    isEntityAccountRoute() {
      return (
        this.$route.name === MENUS.CLIENT.ENTITY_INFO.id ||
        this.$route.name === MENUS.CLIENT.AUTHORIZED_SIGNERS.id
      );
    },
    beneficiaryAccounts() {
      // Only include accounts that actually have a Primary Beneficiary page in the template
      if (!this.clientData || !this.clientData.accounts) return [];
      return this.clientData.accounts.filter(
        (a) =>
          this.getPage(
            MENUS.CLIENT.ACCOUNT_PRIMARY_BENEFICIARIES.id,
            a.instance
          ) !== undefined
      );
    },
    pershingBeneficiaryAccounts() {
      // Only include accounts that actually have a Pershing Primary Beneficiary page in the template
      if (!this.clientData || !this.clientData.accounts) return [];
      return this.clientData.accounts.filter(
        (a) =>
          this.getPage(
            MENUS.CLIENT.PERSHING_PRIMARY_BENEFICIARIES.id,
            a.instance
          ) !== undefined
      );
    },
    beneficiaryCompletionSteps() {
      let completed = 0;

      // Handle standard beneficiary accounts
      this.beneficiaryAccounts.forEach((a) => {
        if (this.isAccountPrimaryBeneficiaryComplete(a.instance)) completed++;

        const hasContingentPage =
          this.getPage(
            MENUS.CLIENT.ACCOUNT_CONTINGENT_BENEFICIARIES.id,
            a.instance
          ) !== undefined;

        if (
          hasContingentPage &&
          this.isAccountContingentBeneficiaryComplete(a.instance)
        ) {
          completed++;
        }
      });

      // Handle Pershing beneficiary accounts
      this.pershingBeneficiaryAccounts.forEach((a) => {
        if (this.isPershingAccountPrimaryBeneficiaryComplete(a.instance))
          completed++;

        const hasContingentPage =
          this.getPage(
            MENUS.CLIENT.PERSHING_CONTINGENT_BENEFICIARIES.id,
            a.instance
          ) !== undefined;

        if (
          hasContingentPage &&
          this.isPershingAccountContingentBeneficiaryComplete(a.instance)
        ) {
          completed++;
        }
      });

      return completed;
    },
    totalBeneficiarySteps() {
      let steps = 0;

      // Count standard beneficiary steps
      this.beneficiaryAccounts.forEach((a) => {
        steps++; // primary

        const hasContingentPage =
          this.getPage(
            MENUS.CLIENT.ACCOUNT_CONTINGENT_BENEFICIARIES.id,
            a.instance
          ) !== undefined;
        if (hasContingentPage) steps++;
      });

      // Count Pershing beneficiary steps
      this.pershingBeneficiaryAccounts.forEach((a) => {
        steps++; // primary

        const hasContingentPage =
          this.getPage(
            MENUS.CLIENT.PERSHING_CONTINGENT_BENEFICIARIES.id,
            a.instance
          ) !== undefined;
        if (hasContingentPage) steps++;
      });

      return steps;
    },
    isBeneficiaryComplete() {
      return this.beneficiaryCompletionSteps === this.totalBeneficiarySteps;
    },
    canDesignateBeneficiaries() {
      return (
        this.beneficiaryAccounts.length > 0 ||
        this.pershingBeneficiaryAccounts.length > 0
      );
    },
    remainingCompletionSteps() {
      return 0;
    },
    isRemainingComplete() {
      return false;
    },
    incompleteSteps() {
      if (!this.clientProgress) return [];

      const steps = [];

      // Basic Identity steps - only show if page exists and is incomplete
      const hasCitizenshipPage =
        this.getPage(MENUS.CLIENT.CITIZENSHIP.id) !== undefined;
      if (hasCitizenshipPage && !this.isCitizenshipComplete)
        steps.push({
          name: "Citizenship",
          route: { name: MENUS.CLIENT.CITIZENSHIP.id },
        });

      const hasLegalNamePage =
        this.getPage(MENUS.CLIENT.LEGAL_NAME.id) !== undefined;
      if (hasLegalNamePage && !this.isLegalNameComplete)
        steps.push({
          name: "Legal Name",
          route: { name: MENUS.CLIENT.LEGAL_NAME.id },
        });

      const hasLegalAddressPage =
        this.getPage(MENUS.CLIENT.LEGAL_ADDRESS.id) !== undefined;
      if (hasLegalAddressPage && !this.isLegalAddressComplete)
        steps.push({
          name: "Legal Address",
          route: { name: MENUS.CLIENT.LEGAL_ADDRESS.id },
        });

      const hasSSNPage = this.getPage(MENUS.CLIENT.SSN.id) !== undefined;
      if (hasSSNPage && !this.isSSNComplete)
        steps.push({
          name: "Social Security Number",
          route: { name: MENUS.CLIENT.SSN.id },
        });

      const hasDOBPage = this.getPage(MENUS.CLIENT.DOB.id) !== undefined;
      if (hasDOBPage && !this.isDOBComplete)
        steps.push({
          name: "Date of Birth",
          route: { name: MENUS.CLIENT.DOB.id },
        });

      const hasAlternatePhonePage =
        this.getPage(MENUS.CLIENT.ALTERNATE_PHONE.id) !== undefined;
      if (hasAlternatePhonePage && !this.isAlternatePhoneComplete)
        steps.push({
          name: "Alternate Phone(s)",
          route: { name: MENUS.CLIENT.ALTERNATE_PHONE.id },
        });
      /* Employment Status only incomplete if present and not complete */
      const hasEmploymentStatusPage =
        this.getPage(MENUS.CLIENT.EMPLOYMENT_STATUS.id) !== undefined;
      if (hasEmploymentStatusPage && !this.isEmploymentStatusComplete)
        steps.push({
          name: "Employment Status",
          route: { name: MENUS.CLIENT.EMPLOYMENT_STATUS.id },
        });

      /* Employment Status 2 only incomplete if present and not complete */
      const hasEmploymentStatus2Page =
        this.getPage(MENUS.CLIENT.EMPLOYMENT_STATUS_2.id) !== undefined;
      if (hasEmploymentStatus2Page && !this.isEmploymentStatus2Complete)
        steps.push({
          name: "Employment Status",
          route: { name: MENUS.CLIENT.EMPLOYMENT_STATUS_2.id },
        });

      /* Line of Business only incomplete if present and not complete */
      const hasBusinessLinePage =
        this.getPage(MENUS.CLIENT.BUSINESS.id) !== undefined;
      if (hasBusinessLinePage && !this.isBusinessLineComplete)
        steps.push({
          name: "Line of Business",
          route: { name: MENUS.CLIENT.BUSINESS.id },
        });
      /* Annual income only incomplete if present and not complete */
      const hasAnnualIncomePage =
        this.getPage(MENUS.CLIENT.ANNUAL_INCOME.id) !== undefined;
      if (hasAnnualIncomePage && !this.isAnnualIncomeComplete)
        steps.push({
          name: "Annual Income",
          route: { name: MENUS.CLIENT.ANNUAL_INCOME.id },
        });
      /* Source of wealth only incomplete if present and not complete */
      const hasWealthSourcePage =
        this.getPage(MENUS.CLIENT.WEALTH_SOURCE.id) !== undefined;
      if (hasWealthSourcePage && !this.isWealthSourceComplete)
        steps.push({
          name: "Source of Wealth",
          route: { name: MENUS.CLIENT.WEALTH_SOURCE.id },
        });
      /* Politically exposed person only incomplete if present and not complete */
      const hasPePPage =
        this.getPage(MENUS.CLIENT.POLITICALLY_EXPOSED_PERSON.id) !== undefined;
      if (hasPePPage && !this.isPePComplete)
        steps.push({
          name: "Politically Exposed Person",
          route: { name: MENUS.CLIENT.POLITICALLY_EXPOSED_PERSON.id },
        });

      /* Company Page only incomplete if present and not complete */
      const hasCompanyPage =
        this.getPage(MENUS.CLIENT.COMPANY_ADDRESS.id) !== undefined;
      if (hasCompanyPage && !this.isCompanyAddressComplete)
        steps.push({
          name: "Business Name & Address",
          route: { name: MENUS.CLIENT.COMPANY_ADDRESS.id },
        });

      /* Company Page only incomplete if present and not complete */
      const hasPershingCompanyPage =
        this.getPage(MENUS.CLIENT.PERSHING_COMPANY_ADDRESS.id) !== undefined;
      if (hasPershingCompanyPage && !this.isPershingCompanyAddressComplete)
        steps.push({
          name: "Business Name & Address",
          route: { name: MENUS.CLIENT.PERSHING_COMPANY_ADDRESS.id },
        });

      /* Job Page only incomplete if present and not complete */
      const hasJobPage =
        this.getPage(MENUS.CLIENT.JOB_DESCRIPTION.id) !== undefined;
      if (hasJobPage && !this.isJobDescriptionComplete)
        steps.push({
          name: "Job Description",
          route: { name: MENUS.CLIENT.JOB_DESCRIPTION.id },
        });

      /* Company Official only incomplete if present and not complete */
      const hasCompanyOfficialPage =
        this.getPage(MENUS.CLIENT.COMPANY_OFFICIAL.id) !== undefined;
      if (hasCompanyOfficialPage && !this.isCompanyOfficialComplete)
        steps.push({
          name: "Publicly Held Company",
          route: { name: MENUS.CLIENT.COMPANY_OFFICIAL.id },
        });

      /* Beneficial Interest only incomplete if present and not complete */
      const hasBeneficialInterestPage =
        this.getPage(MENUS.CLIENT.BENEFICIAL_INTEREST.id) !== undefined;
      if (hasBeneficialInterestPage && !this.isBeneficialInterestComplete)
        steps.push({
          name: "Associations",
          route: { name: MENUS.CLIENT.BENEFICIAL_INTEREST.id },
        });

      // === Add single Pershing pages ===
      /* Pershing Marital Status only incomplete if present and not complete */
      const hasPershingMaritalStatusPage =
        this.getPage(MENUS.CLIENT.PERSHING_MARITAL_STATUS.id) !== undefined;
      if (hasPershingMaritalStatusPage && !this.isPershingMaritalStatusComplete)
        steps.push({
          name: "Marital Status",
          route: { name: MENUS.CLIENT.PERSHING_MARITAL_STATUS.id },
        });

      /* Pershing Employer Risk only incomplete if present and not complete */
      const hasPershingEmployerRiskPage =
        this.getPage(MENUS.CLIENT.PERSHING_EMPLOYER_RISK.id) !== undefined;
      if (hasPershingEmployerRiskPage && !this.isPershingEmployerRiskComplete)
        steps.push({
          name: "Employer Industry Increased Risk",
          route: { name: MENUS.CLIENT.PERSHING_EMPLOYER_RISK.id },
        });

      const accounts = this.clientData.accounts;
      const getAccountTypeName = (data) => {
        if (!data) return null;
        const name = data.instance || data.name;
        if (name === DB_ACCOUNT_TYPES.ROTH_IRA)
          return `${ACCOUNT_TYPES.ROTH_IRA} (${data.label})`;
        else if (name === DB_ACCOUNT_TYPES.IRA)
          return `${ACCOUNT_TYPES.IRA} (${data.label})`;
        else if (name === DB_ACCOUNT_TYPES.SEP_IRA)
          return `${ACCOUNT_TYPES.SEP_IRA} (${data.label})`;
        else if (name === DB_ACCOUNT_TYPES.SINGLE_NAME_BROKERAGE)
          return `${ACCOUNT_TYPES.SINGLE_NAME_BROKERAGE} (${data.label})`;
        else if (name === DB_ACCOUNT_TYPES.JOINT_NAME_BROKERAGE)
          return `${ACCOUNT_TYPES.JOINT_NAME_BROKERAGE} (${data.label})`;
        else if (name === DB_ACCOUNT_TYPES.MANAGED_BROKERAGE)
          return `${ACCOUNT_TYPES.MANAGED_BROKERAGE} (${data.label})`;
        else if (name === DB_ACCOUNT_TYPES.MANAGED_JOINT)
          return `${ACCOUNT_TYPES.MANAGED_JOINT} (${data.label})`;
        else if (name === DB_ACCOUNT_TYPES.MANAGED_IRA)
          return `${ACCOUNT_TYPES.MANAGED_IRA} (${data.label})`;
        else if (name === DB_ACCOUNT_TYPES.MANAGED_ROTH_IRA)
          return `${ACCOUNT_TYPES.MANAGED_ROTH_IRA} (${data.label})`;
        else if (name === DB_ACCOUNT_TYPES.MANAGED_TRUST)
          return `${ACCOUNT_TYPES.MANAGED_TRUST} (${data.label})`;
        else if (name === DB_ACCOUNT_TYPES.OFFLINE_CORPORATE)
          return `${ACCOUNT_TYPES.OFFLINE_CORPORATE} (${data.label})`;
        else if (name === DB_ACCOUNT_TYPES.OFFLINE_INHERITED_IRA)
          return `${ACCOUNT_TYPES.OFFLINE_INHERITED_IRA} (${data.label})`;
        else if (name === DB_ACCOUNT_TYPES.OFFLINE_TRUST)
          return `${ACCOUNT_TYPES.OFFLINE_TRUST} (${data.label})`;
        else if (name === DB_ACCOUNT_TYPES.JOINT_NAME_BROKERAGE)
          return `${ACCOUNT_TYPES.JOINT_NAME_BROKERAGE} (${data.label})`;
        else if (name === DB_ACCOUNT_TYPES.SINGLE_NAME_BROKERAGE)
          return `${ACCOUNT_TYPES.SINGLE_NAME_BROKERAGE} (${data.label})`;
        else if (name === DB_ACCOUNT_TYPES.OFFLINE_SIMPLE_IRA)
          return `${ACCOUNT_TYPES.OFFLINE_SIMPLE_IRA} (${data.label})`;
        else if (name === DB_ACCOUNT_TYPES.OFFLINE_OTHER)
          return `${ACCOUNT_TYPES.OFFLINE_OTHER} (${data.label})`;
        return `${name} (${data.label})`;
      };
      accounts.forEach((a) => {
        const primaryRouteInfo = {
          name: MENUS.CLIENT.ACCOUNT_PRIMARY_BENEFICIARIES.id,
          params: { name: a.instance.name, label: a.instance.label },
        };
        if (!this.isAccountPrimaryBeneficiaryComplete(a.instance))
          steps.push({
            name: `${getAccountTypeName(a.instance)} Primary Beneficiaries`,
            route: primaryRouteInfo,
          });

        /* Contingent Beneficiary Page only incomplete if present and not complete */
        const contingentRouteInfo = {
          name: MENUS.CLIENT.ACCOUNT_CONTINGENT_BENEFICIARIES.id,
          params: { name: a.instance.name, label: a.instance.label },
        };
        const hasContingentBeneficiaryPage =
          this.getPage(
            MENUS.CLIENT.ACCOUNT_CONTINGENT_BENEFICIARIES.id,
            a.instance
          ) !== undefined;
        if (
          hasContingentBeneficiaryPage &&
          !this.isAccountContingentBeneficiaryComplete(a.instance)
        )
          steps.push({
            name: `${getAccountTypeName(a.instance)} Contingent Beneficiaries`,
            route: contingentRouteInfo,
          });
      });

      // === Add Pershing Beneficiary pages ===
      this.pershingBeneficiaryAccounts.forEach((a) => {
        /* Primary Beneficiary Page only incomplete if present and not complete */
        const primaryRouteInfo = {
          name: MENUS.CLIENT.PERSHING_PRIMARY_BENEFICIARIES.id,
          params: { name: a.instance.name, label: a.instance.label },
        };
        if (!this.isPershingAccountPrimaryBeneficiaryComplete(a.instance))
          steps.push({
            name: `${getAccountTypeName(a.instance)} Primary Beneficiaries`,
            route: primaryRouteInfo,
          });

        /* Pershing Contingent Beneficiary Page only incomplete if present and not complete */
        const contingentRouteInfo = {
          name: MENUS.CLIENT.PERSHING_CONTINGENT_BENEFICIARIES.id,
          params: { name: a.instance.name, label: a.instance.label },
        };
        const hasContingentBeneficiaryPage =
          this.getPage(
            MENUS.CLIENT.PERSHING_CONTINGENT_BENEFICIARIES.id,
            a.instance
          ) !== undefined;
        if (
          hasContingentBeneficiaryPage &&
          !this.isPershingAccountContingentBeneficiaryComplete(a.instance)
        )
          steps.push({
            name: `${getAccountTypeName(a.instance)} Contingent Beneficiaries`,
            route: contingentRouteInfo,
          });
      });

      // === Add Pershing Account-specific pages ===
      if (this.clientData && this.clientData.accounts) {
        this.clientData.accounts.forEach((a) => {
          // Check for KYC pages
          const hasKycPage =
            this.getPage(MENUS.CLIENT.PERSHING_KYC.id, a.instance) !==
            undefined;
          if (hasKycPage && !this.isPershingKycComplete(a.instance)) {
            steps.push({
              name: `${getAccountTypeName(a.instance)} KYC`,
              route: {
                name: MENUS.CLIENT.PERSHING_KYC.id,
                params: { name: a.instance.name, label: a.instance.label },
              },
            });
          }

          // Check for Joint Account Info pages
          const hasJointAccountInfoPage =
            this.getPage(
              MENUS.CLIENT.PERSHING_JOINT_ACCOUNT_INFO.id,
              a.instance
            ) !== undefined;
          if (
            hasJointAccountInfoPage &&
            !this.isPershingJointAccountInfoComplete(a.instance)
          ) {
            steps.push({
              name: `${getAccountTypeName(a.instance)} Joint Account Info`,
              route: {
                name: MENUS.CLIENT.PERSHING_JOINT_ACCOUNT_INFO.id,
                params: { name: a.instance.name, label: a.instance.label },
              },
            });
          }
        });
      }

      // === Add Decedent pages ===
      this.decedentPages.forEach((page) => {
        if (!(page.lastCompleted && page.status === "SYNCED")) {
          const accountLabel = getAccountTypeName(page?.data) || "Account";
          steps.push({
            name: `${accountLabel} Decedent Details`,
            route: {
              name: page.pageName,
              params: { name: page.instance, label: page.label },
            },
          });
        }
      });

      // === Add Trust Account pages ===
      this.trustAccountPages.forEach((page) => {
        if (!(page.lastCompleted && page.status === "SYNCED")) {
          let pageName = "";
          if (
            page.pageName === MENUS.CLIENT.TRUST_INFO.id ||
            page.pageName === MENUS.CLIENT.PERSHING_TRUST_INFO.id
          ) {
            pageName = "Trust Information";
          } else if (page.pageName === MENUS.CLIENT.TRUSTEES.id) {
            pageName = "Trustees";
          } else if (page.pageName === MENUS.CLIENT.GRANTORS.id) {
            pageName = "Grantors";
          }
          const accountLabel = getAccountTypeName(page?.data) || "Account";
          steps.push({
            name: `${accountLabel} ${pageName}`,
            route: {
              name: page.pageName,
              params: { name: page.instance, label: page.label },
            },
          });
        }
      });

      // === Add Entity Account pages ===
      this.entityAccountPages.forEach((page) => {
        if (!(page.lastCompleted && page.status === "SYNCED")) {
          let pageName = "";
          if (page.pageName === MENUS.CLIENT.ENTITY_INFO.id) {
            pageName = "Entity Information";
          } else if (page.pageName === MENUS.CLIENT.AUTHORIZED_SIGNERS.id) {
            pageName = "Authorized Signers";
          }
          const accountLabel = getAccountTypeName(page?.data) || "Account";
          steps.push({
            name: `${accountLabel} ${pageName}`,
            route: {
              name: page.pageName,
              params: { name: page.instance, label: page.label },
            },
          });
        }
      });

      /* Consolidated Statement only incomplete if present and not complete */
      const hasConsolidatedStatementPage =
        this.getPage(MENUS.CLIENT.CONSOLIDATED_STATEMENT.id) !== undefined;
      if (hasConsolidatedStatementPage && !this.isConsolidatedStatementComplete)
        steps.push({
          name: "Consolidated Statement",
          route: { name: MENUS.CLIENT.CONSOLIDATED_STATEMENT.id },
        });

      /* Pershing Consolidated Statement only incomplete if present and not complete */
      const hasPershingConsolidatedStatementPage =
        this.getPage(MENUS.CLIENT.PERSHING_CONSOLIDATED_STATEMENT.id) !== undefined;
      if (hasPershingConsolidatedStatementPage && !this.isPershingConsolidatedStatementComplete)
        steps.push({
          name: "Consolidated Statement",
          route: { name: MENUS.CLIENT.PERSHING_CONSOLIDATED_STATEMENT.id },
        });

      if (!this.areCustomQuestionsComplete) {
        const customQuestions = this.clientProgress
          ? this.clientProgress.progress.filter(
              (page) => page.pageName === MENUS.CLIENT.CUSTOM_QUESTIONS.id
            )
          : [];
        customQuestions.forEach((page) => {
          const { _id, title } = page.data.question;
          // Include questions that are not fully synced (our bug fix)
          if (!page.lastCompleted || page.status !== "SYNCED") {
            steps.push({
              name: title,
              route: {
                name: MENUS.CLIENT.CUSTOM_QUESTIONS.id,
                params: { questionId: _id },
              },
            });
          }
        });
      }
      return steps;
    },
    requiredDocuments() {
      const requiredDocuments = this.clientProgress
        ? this.clientProgress.uploads.map(({ name, updatedAt, ...rest }) => ({
            id: name,
            name: name.replace(
              /(?<=[a-z])(?=[A-Z])|(?<=[A-Z])(?=[A-Z][a-z])/g,
              " "
            ),
            uploaded: updatedAt ? formatDate(updatedAt, US_DATE_SHORT) : null,
            ...rest,
          }))
        : [];

      return requiredDocuments;
    },
    isLegalRoute() {
      let r = false;
      switch (this.$route.name) {
        case MENUS.CLIENT.CITIZENSHIP.id:
        case MENUS.CLIENT.LEGAL_NAME.id:
        case MENUS.CLIENT.LEGAL_ADDRESS.id:
          r = true;
          break;
      }
      return r;
    },
    isBasicRoute() {
      let r = false;
      switch (this.$route.name) {
        case MENUS.CLIENT.SSN.id:
        case MENUS.CLIENT.DOB.id:
        case MENUS.CLIENT.ALTERNATE_PHONE.id:
          r = true;
          break;
      }
      return r;
    },
    isEmploymentRoute() {
      let r = false;
      switch (this.$route.name) {
        case MENUS.CLIENT.EMPLOYMENT_STATUS.id:
        case MENUS.CLIENT.COMPANY_ADDRESS.id:
        case MENUS.CLIENT.PERSHING_COMPANY_ADDRESS.id:
        case MENUS.CLIENT.JOB_DESCRIPTION.id:
        case MENUS.CLIENT.COMPANY_OFFICIAL.id:
        case MENUS.CLIENT.BENEFICIAL_INTEREST.id:
          r = true;
          break;
      }
      return r;
    },
    firstBeneficiaryRoute() {
      if (this.beneficiaryAccounts.length > 0) {
        const firstAccount = this.beneficiaryAccounts[0];
        return {
          name: MENUS.CLIENT.ACCOUNT_PRIMARY_BENEFICIARIES.id,
          params: {
            name: firstAccount.instance.name,
            label: firstAccount.instance.label,
          },
        };
      } else if (this.pershingBeneficiaryAccounts.length > 0) {
        const firstAccount = this.pershingBeneficiaryAccounts[0];
        return {
          name: MENUS.CLIENT.PERSHING_PRIMARY_BENEFICIARIES.id,
          params: {
            name: firstAccount.instance.name,
            label: firstAccount.instance.label,
          },
        };
      }

      return null;
    },
    lastBeneficiaryRoute() {
      if (this.beneficiaryAccounts.length > 0) {
        const lastAccount =
          this.beneficiaryAccounts[this.beneficiaryAccounts.length - 1];
        const hasContingentBeneficiaryPage =
          this.getPage(
            MENUS.CLIENT.ACCOUNT_CONTINGENT_BENEFICIARIES.id,
            lastAccount.instance
          ) !== undefined;

        // Is last account has contingent beneficiary page (if present)
        // Otherwise last account primary beneficiary page
        return hasContingentBeneficiaryPage
          ? {
              name: MENUS.CLIENT.ACCOUNT_CONTINGENT_BENEFICIARIES.id,
              params: {
                name: lastAccount.instance.name,
                label: lastAccount.instance.label,
              },
            }
          : {
              name: MENUS.CLIENT.ACCOUNT_PRIMARY_BENEFICIARIES.id,
              params: {
                name: lastAccount.instance.name,
                label: lastAccount.instance.label,
              },
            };
      }
      return null;
    },
    isBeneficiaryRoute() {
      let r = false;
      switch (this.$route.name) {
        case MENUS.CLIENT.ACCOUNT_PRIMARY_BENEFICIARIES.id:
        case MENUS.CLIENT.ACCOUNT_CONTINGENT_BENEFICIARIES.id:
        case MENUS.CLIENT.PERSHING_PRIMARY_BENEFICIARIES.id:
        case MENUS.CLIENT.PERSHING_CONTINGENT_BENEFICIARIES.id:
          r = true;
          break;
      }
      return r;
    },
    isAccountDetailsRoute() {
      let r = false;
      switch (this.$route.name) {
        case MENUS.CLIENT.TRUST_INFO.id:
        case MENUS.CLIENT.PERSHING_TRUST_INFO.id:
        case MENUS.CLIENT.TRUSTEES.id:
        case MENUS.CLIENT.GRANTORS.id:
        case MENUS.CLIENT.ENTITY_INFO.id:
        case MENUS.CLIENT.AUTHORIZED_SIGNERS.id:
          r = true;
          break;
      }
      return r;
    },
    isFinishRoute() {
      return this.$route.name === MENUS.CLIENT.FINISH.id;
    },
  },

  methods: {
    isFirstPage(pageName, instance = null, questionId = null) {
      return (
        getClientPageIndex(
          this.clientProgress,
          pageName,
          instance,
          questionId
        ) === 0
      );
    },
    /*
      Get a page from the page list
    */
    getPage(pageName, instance = null, question = null) {
      return getClientPage(this.clientProgress, pageName, instance, question);
    },

    /* 
      Check if a page is complete (filled and synced to CRM)
      
      Args:
        pageName: The name/ID of the page to check
        instance: Optional instance for pages that can have multiple instances
        question: Optional questionId for custom questions
        
      Returns:
        true if:
          1. The page doesn't exist (not required for this interview flow)
          2. OR (page exists AND has been filled AND is synced)
        false otherwise
    */
    isPageComplete(pageName, instance = null, question = null) {
      // No progress data means nothing is complete
      if (!this.clientProgress) return false;

      // Get the page from the progress data
      const progressPage = this.getPage(pageName, instance, question);

      // If page doesn't exist, it's not required for this interview flow
      if (!progressPage) return true;

      // Page exists - it's complete only if it's been filled AND synced
      return (
        progressPage.lastCompleted !== null && progressPage.status === "SYNCED"
      );
    },

    /*
      Is Primary Beneficiary Page complete for this account instance
    */
    isAccountPrimaryBeneficiaryComplete(instance) {
      return this.isPageComplete(
        MENUS.CLIENT.ACCOUNT_PRIMARY_BENEFICIARIES.id,
        instance
      );
    },

    /*
      Is Contingent Beneficiary Page complete for this account instance
    */
    isAccountContingentBeneficiaryComplete(instance) {
      return this.isPageComplete(
        MENUS.CLIENT.ACCOUNT_CONTINGENT_BENEFICIARIES.id,
        instance
      );
    },

    /*
      Is Pershing Primary Beneficiary Page complete for this account instance
    */
    isPershingAccountPrimaryBeneficiaryComplete(instance) {
      return this.isPageComplete(
        MENUS.CLIENT.PERSHING_PRIMARY_BENEFICIARIES.id,
        instance
      );
    },

    /*
      Is Pershing Contingent Beneficiary Page complete for this account instance
    */
    isPershingAccountContingentBeneficiaryComplete(instance) {
      return this.isPageComplete(
        MENUS.CLIENT.PERSHING_CONTINGENT_BENEFICIARIES.id,
        instance
      );
    },
    isPershingKycComplete(instance) {
      return this.isPageComplete(MENUS.CLIENT.PERSHING_KYC.id, instance);
    },
    isPershingJointAccountInfoComplete(instance) {
      return this.isPageComplete(
        MENUS.CLIENT.PERSHING_JOINT_ACCOUNT_INFO.id,
        instance
      );
    },

    /*
      Is Inherited IRA (Decedent) Page complete for this account instance
    */
    isInheritedIRAComplete(instance) {
      return this.isPageComplete(MENUS.CLIENT.INHERITEDIRA.id, instance);
    },

    /*
      Is Trust Info Page complete for this account instance
    */
    isTrustInfoComplete(instance) {
      return this.isPageComplete(MENUS.CLIENT.TRUST_INFO.id, instance);
    },

    /*
      Is Trustees Page complete for this account instance
    */
    isTrusteesComplete(instance) {
      return this.isPageComplete(MENUS.CLIENT.TRUSTEES.id, instance);
    },

    /*
      Is Grantors Page complete for this account instance
    */
    isGrantorsComplete(instance) {
      return this.isPageComplete(MENUS.CLIENT.GRANTORS.id, instance);
    },

    /*
      Is Entity Info Page complete for this account instance
    */
    isEntityInfoComplete(instance) {
      return this.isPageComplete(MENUS.CLIENT.ENTITY_INFO.id, instance);
    },

    /*
      Is Authorized Signers Page complete for this account instance
    */
    isAuthorizedSignersComplete(instance) {
      return this.isPageComplete(MENUS.CLIENT.AUTHORIZED_SIGNERS.id, instance);
    },
  },

  created() {},
};
export default UnathenticatedClientMixin;
